import React, { useCallback, useMemo, useState } from 'react';
import Select, {
  components,
  GroupBase,
  MenuListProps,
  MultiValue,
  StylesConfig,
} from 'react-select';
import { Checkbox } from 'src/ui/components/Checkbox';

export type AccountOption = {
  label: string;
  value: string | number;
};

interface Props {
  options: AccountOption[];
  selectedValues: AccountOption[];
  onChange: (values: AccountOption[]) => void;
  isLoading?: boolean;
}

const SELECT_ALL_VALUE = '__all__';

const standardizeString = (str: string) =>
  str.toLowerCase().replace(/[\s-]/g, '');

export default function SearchableGroupedSelect({
  options,
  selectedValues,
  onChange,
  isLoading,
}: Props) {
  const [searchText, setSearchText] = useState('');

  const visibleOptions = useMemo(() => {
    if (!searchText) return options;
    const s = standardizeString(searchText);
    return options.filter(o => standardizeString(o.label).includes(s));
  }, [options, searchText]);

  const isAllVisibleSelected = useMemo(() => {
    if (!visibleOptions.length) return false;
    return visibleOptions.every(opt =>
      selectedValues.some(sel => sel.value === opt.value),
    );
  }, [visibleOptions, selectedValues]);

  const groupedOptions: GroupBase<AccountOption>[] = useMemo(() => {
    const selectAllOption: AccountOption = {
      label: 'Select All',
      value: SELECT_ALL_VALUE,
    };

    const selected: AccountOption[] = [];
    const unselected: AccountOption[] = [];

    visibleOptions.forEach(opt => {
      if (selectedValues.some(s => s.value === opt.value)) {
        selected.push(opt);
      } else {
        unselected.push(opt);
      }
    });

    return [
      { label: '', options: [selectAllOption] },
      ...(selected.length
        ? [
            {
              label: 'SELECTED ACCOUNTS',
              options: selected,
            },
          ]
        : []),
      { label: '', options: unselected },
    ];
  }, [visibleOptions, selectedValues]);

  const Option = (props: any) => {
    const isSelectAll = props.data.value === SELECT_ALL_VALUE;

    return (
      <components.Option {...props}>
        <Checkbox
          id={props.label}
          checked={isSelectAll ? isAllVisibleSelected : props.isSelected}
          label={props.label}
          readOnly
        />
      </components.Option>
    );
  };

  const MenuList = React.memo((props: MenuListProps<AccountOption>) => (
    <components.MenuList {...props}>{props.children}</components.MenuList>
  ));

  MenuList.displayName = 'SearchableGroupedSelectMenuList';

  const MultiValue = () => null;

  const handleChange = useCallback(
    (_val: MultiValue<AccountOption>, meta: any) => {
      if (
        meta.action !== 'select-option' &&
        meta.action !== 'deselect-option'
      ) {
        return;
      }

      const option = meta.option as AccountOption | undefined;
      if (!option) return;

      // SELECT ALL
      if (option.value === SELECT_ALL_VALUE) {
        if (isAllVisibleSelected) {
          onChange(
            selectedValues.filter(
              s => !visibleOptions.some(v => v.value === s.value),
            ),
          );
        } else {
          onChange([
            ...selectedValues,
            ...visibleOptions.filter(
              v => !selectedValues.some(s => s.value === v.value),
            ),
          ]);
        }
        return;
      }

      // DESELECT
      if (meta.action === 'deselect-option') {
        onChange(selectedValues.filter(s => s.value !== option.value));
        return;
      }

      // SELECT
      if (meta.action === 'select-option') {
        onChange([...selectedValues, option]);
      }
    },
    [selectedValues, visibleOptions, isAllVisibleSelected, onChange],
  );

  const handleInputChange = (value: string, action: any) => {
    if (action.action === 'input-change') {
      setSearchText(value);
      return value;
    }
    return searchText;
  };

  const styles: StylesConfig<AccountOption, true> = {
    control: base => ({ ...base }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#d9d9d9' : 'white',
      color: 'black',
    }),
    groupHeading: base => ({
      ...base,
      color: '#666',
    }),
    multiValue: () => ({ display: 'none' }),
    multiValueLabel: () => ({ display: 'none' }),
    multiValueRemove: () => ({ display: 'none' }),
  };

  const ClearSearchButton = React.memo(
    ({ onClear }: { onClear: () => void }) => (
      <span
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
          onClear();
        }}
        style={{
          cursor: 'pointer',
          padding: '0 6px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
        }}
        aria-label="Clear search"
      >
        ✕
      </span>
    ),
  );

  ClearSearchButton.displayName = 'ClearSearchButton';

  const IndicatorsContainer = React.memo((props: any) => {
    const { children, selectProps } = props;

    const searchText = selectProps.inputValue as string;
    const clearSearch = () => {
      selectProps.onInputChange('', { action: 'input-change' });
    };

    return (
      <components.IndicatorsContainer {...props}>
        {searchText ? <ClearSearchButton onClear={clearSearch} /> : null}
        {children}
      </components.IndicatorsContainer>
    );
  });

  IndicatorsContainer.displayName = 'SearchIndicatorsContainer';

  const placeholderText =
    selectedValues.length > 0
      ? `Selected (${selectedValues.length})`
      : 'Select';

  return (
    <Select<AccountOption, true>
      isMulti
      value={selectedValues}
      options={groupedOptions}
      inputValue={searchText}
      onInputChange={handleInputChange}
      onChange={handleChange}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isLoading={isLoading}
      isDisabled={isLoading}
      placeholder={isLoading ? 'Loading...' : placeholderText}
      components={{
        Option,
        MenuList,
        MultiValue,
        IndicatorsContainer,
        ClearIndicator: () => null,
      }}
      styles={styles}
      controlShouldRenderValue={false}
      openMenuOnFocus={false}
      filterOption={null}
      classNamePrefix="searchable-grouped-select"
      className="searchable-grouped-select"
    />
  );
}
